import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { usePaged } from "@/lib/hooks";
import { useEntityAction } from "@/lib/mutations";
import { useAuth } from "@/auth/auth";
import { PagedTable } from "@/components/PagedTable";
import { RowActions } from "@/components/RowActions";
import { BookingStatusBadge } from "@/components/ui";
import i18n, { tStatus } from "@/i18n";
import type { Booking } from "@/lib/types";
import { companyToday, formatCompanyDate } from "@/lib/time";

// The statuses the owner sees (derived from the lines — see the API's
// `display_status`), plus the stored ones a booking keeps when it has no lines.
const BOOKING_STATUSES = [
  "attention", "upcoming", "partial", "done",
  "draft", "awaiting_information", "awaiting_approval", "completed",
  "cancelled", "refunded",
];

const col = createColumnHelper<Booking>();

/** "1 Sep – 4 Sep" / "1 Sep" / null. Uses the service-date span of the
 * booking's lines when it has any (`date_from` / `date_to`), else its own
 * check-in / check-out. Plain calendar dates — no time zone is involved. */
export function dateRange(b: {
  start_date: string | null;
  end_date: string | null;
  date_from?: string | null;
  date_to?: string | null;
}): string | null {
  const from = b.date_from ?? b.start_date;
  const to = b.date_to ?? b.end_date;
  if (!from) return null;
  const thisYear = companyToday().slice(0, 4);
  const fmt = (s: string) =>
    new Date(Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10))).toLocaleDateString(
      i18n.language,
      {
        timeZone: "UTC",
        day: "numeric",
        month: "short",
        ...(s.slice(0, 4) === thisYear ? {} : { year: "numeric" }),
      },
    );
  if (!to || to === from) return fmt(from);
  return `${fmt(from)} – ${fmt(to)}`;
}

export default function BookingsListPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canWrite = can("bookings:write");
  const [status, setStatus] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const paged = usePaged<Booking>(["bookings"], "/bookings", {
    display_status: status || undefined,
    include_deleted: showDeleted ? "true" : undefined,
  });
  const action = useEntityAction([["bookings"]]);

  const columns = useMemo(
    () => [
      col.accessor("booking_number", {
        header: () => t("fields.number"),
        cell: (c) => (
          <Link
            className="font-medium text-sand hover:text-gold"
            to={`/bookings/${c.row.original.booking_number}`}
          >
            {c.getValue()}
          </Link>
        ),
      }),
      col.accessor("customer_name", {
        header: () => t("fields.customer"),
        // A required field left empty is flagged, not shown as a quiet dash.
        cell: (c) =>
          c.getValue() ?? (
            <span className="text-amber-700">{t("bookings.noCustomer")}</span>
          ),
      }),
      col.accessor("status", {
        header: () => t("fields.status"),
        cell: (c) => <BookingStatusBadge booking={c.row.original} />,
      }),
      col.display({
        id: "lines",
        header: () => t("bookings.servicesDone"),
        cell: (c) => {
          const b = c.row.original;
          return b.lines_total ? `${b.lines_done ?? 0} / ${b.lines_total}` : "—";
        },
      }),
      col.accessor("currency", { header: () => t("fields.currency") }),
      col.display({
        id: "travel",
        header: () => t("fields.travelDates"),
        cell: (c) => dateRange(c.row.original) ?? "—",
      }),
      col.accessor("created_at", {
        header: () => t("fields.created"),
        cell: (c) => formatCompanyDate(c.getValue()),
      }),
      ...(canWrite
        ? [
            col.display({
              id: "actions",
              header: () => t("common.actions"),
              cell: (c) => {
                const ref = c.row.original.booking_number;
                return (
                  <RowActions
                    deleted={!!c.row.original.deleted_at}
                    canWrite={canWrite}
                    busy={action.isPending}
                    onDelete={() => action.mutate(() => api.del(`/bookings/${ref}`))}
                    onRestore={() =>
                      action.mutate(() => api.post(`/bookings/${ref}/restore`))
                    }
                  />
                );
              },
            }),
          ]
        : []),
    ],
    [t, canWrite, action],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t("bookings.title")}</h1>
        {canWrite && (
          // One entry point: the multi-line order form (WhatsApp / manual).
          <Link
            to="/bookings/quick"
            className="rounded-md bg-gold px-3 py-1.5 text-xs font-semibold text-olive-900 hover:bg-gold-300"
          >
            + {t("bookings.new")}
          </Link>
        )}
      </div>
      <PagedTable
        columns={columns}
        data={paged.items}
        query={paged.query}
        total={paged.total}
        page={paged.page}
        pageCount={paged.pageCount}
        onPage={paged.setPage}
        empty={t("bookings.empty")}
        toolbar={
          <div className="flex items-center gap-3">
            <select
              className="rounded-md border border-olive-600 bg-olive-800 px-2 py-1 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                paged.resetPage();
              }}
            >
              <option value="">{t("common.allStatuses")}</option>
              {BOOKING_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {tStatus(s)}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-sand-muted">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => {
                  setShowDeleted(e.target.checked);
                  paged.resetPage();
                }}
              />
              {t("common.showDeleted")}
            </label>
          </div>
        }
      />
    </div>
  );
}
